import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Users, Plus, Search, Lock, ChevronUp, ChevronDown, MoreVertical, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { partiesAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import AddPartyModal from '../../components/modals/AddPartyModal';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { usePermissions } from '../../contexts/PermissionsContext';

const PartiesList = () => {
  const { businessId } = useParams();
  const { canView, canCreate, canEdit, canDelete, isOwner, isStaff } = usePermissions();
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedParty, setSelectedParty] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  
  // Collapsible header state
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(() => {
    const saved = localStorage.getItem('header_collapsed_parties');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('header_collapsed_parties', String(isHeaderCollapsed));
  }, [isHeaderCollapsed]);

  // Permission checks for this module
  const canViewParties = isOwner || canView('customers');
  const canCreateParty = isOwner || canCreate('customers');
  const canEditParty = isOwner || canEdit('customers');
  const canDeleteParty = isOwner || canDelete('customers');

  useEffect(() => {
    if (canViewParties) {
      fetchParties();
    }
  }, [typeFilter, searchQuery, canViewParties]);

  const fetchParties = async () => {
    try {
      setLoading(true);
      const params = {};
      if (typeFilter && typeFilter !== 'all') params.type = typeFilter;
      if (searchQuery) params.q = searchQuery;
      
      const response = await partiesAPI.list(params);
      setParties(response.data);
    } catch (error) {
      console.error('Error fetching parties:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load parties'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddParty = () => {
    if (!canCreateParty) {
      toast.error('You do not have permission to create parties');
      return;
    }
    setSelectedParty(null);
    setShowModal(true);
  };

  const handleEditParty = (party) => {
    if (!canEditParty) {
      toast.error('You do not have permission to edit parties');
      return;
    }
    setSelectedParty(party);
    setShowModal(true);
  };

  const handleSaveParty = () => {
    setShowModal(false);
    fetchParties();
  };

  const handleDeleteParty = (party) => {
    setConfirmModal({
      title: 'Delete Party',
      description: `Permanently delete "${party.name}"? This cannot be undone if the party has linked invoices or payments.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await partiesAPI.delete(party.id);
          toast.success('Party deleted');
          fetchParties();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete party'));
        }
      },
    });
  };

  const getBalanceColor = (balance) => {
    if (balance > 0) return 'text-green-600';
    if (balance < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getBalanceText = (balance) => {
    if (balance > 0) return `₹${balance.toFixed(2)} To Receive`;
    if (balance < 0) return `₹${Math.abs(balance).toFixed(2)} To Pay`;
    return '₹0';
  };

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="parties-list-page">
      {/* Access Denied for Staff without permission */}
      {!canViewParties && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <Lock className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Restricted</h2>
          <p className="text-red-600">You don&apos;t have permission to view contacts. Please contact your administrator.</p>
        </div>
      )}

      {canViewParties && (
        <>
          {/* ════════════════════════════════════════════════════════════════════════
              COLLAPSIBLE HEADER SECTION
              ════════════════════════════════════════════════════════════════════════ */}
          
          {/* Collapsed State - Minimal Header */}
          {isHeaderCollapsed && (
            <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm animate-fadeIn">
              <div className="flex items-center gap-4">
                <h1 className="text-[15px] font-medium text-gray-700">Parties</h1>
                <span className="text-[12px] text-gray-400 hidden sm:inline">• {parties.length} contacts</span>
              </div>
              <div className="flex items-center gap-3">
                {canCreateParty && (
                  <button onClick={handleAddParty} className="premium-button-primary text-[13px] py-2">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Party
                  </button>
                )}
                <button
                  onClick={() => setIsHeaderCollapsed(false)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                  Expand
                </button>
              </div>
            </div>
          )}

          {/* Expanded State - Full Header */}
          <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isHeaderCollapsed ? 'max-h-0 opacity-0' : 'max-h-[500px] opacity-100'}`}>
            {/* Header - CLASSICAL PREMIUM */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between relative">
              {/* Collapse Button */}
              <button
                onClick={() => setIsHeaderCollapsed(true)}
                className="absolute top-0 right-0 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                <ChevronUp className="w-3.5 h-3.5" />
                Collapse
              </button>
              
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Parties</h1>
                <p className="text-sm text-gray-600 mt-1.5">Manage your customers and suppliers</p>
              </div>
              {canCreateParty && (
                <button 
                  onClick={handleAddParty} 
                  data-testid="add-party-button" 
                  className="premium-button-primary w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Party
                </button>
              )}
            </div>

            {/* Filters - REFINED COMPACT */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-4 py-3 mt-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 premium-search-bar">
                  <Search className="premium-search-icon" />
                  <input
                    type="text"
                    placeholder="Search by name, phone, or GSTIN..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="premium-search-input"
                    data-testid="parties-search-input"
                  />
                </div>
                <select 
                  value={typeFilter} 
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="premium-select w-full md:w-44"
                  data-testid="parties-type-filter"
                >
                  <option value="all">All Types</option>
                  <option value="customer">Customers</option>
                  <option value="supplier">Suppliers</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>
          </div>

          {/* Animation styles */}
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(-4px); }
              to { opacity: 1; transform: translateY(0); }
            }
            .animate-fadeIn {
              animation: fadeIn 0.2s ease-out;
            }
          `}</style>

      {/* Parties List */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading parties...</p>
              </div>
            </div>
          ) : parties.length === 0 ? (
            <div className="premium-empty-state">
              <Users className="premium-empty-icon" />
              <h3 className="premium-empty-title">No parties found</h3>
              <p className="premium-empty-description">Add your customers and suppliers to start managing your business relationships</p>
              {canCreateParty && (
                <Button onClick={handleAddParty} className="premium-button-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Party
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table View - FROSTED GLASS PREMIUM SURFACE */}
              <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[35%]">Party Name</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[20%]">Phone</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[12%]">Type</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[20%]">Balance</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[13%]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {parties.map((party) => (
                      <tr
                        key={party.id}
                        data-testid={`party-row-${party.id}`}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium text-gray-900">{party.name}</div>
                            {party.gstin && (
                              <div className="text-xs text-gray-500 mt-0.5">GSTIN: {party.gstin}</div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {party.phone || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                            {party.type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={`font-medium ${getBalanceColor(party.current_balance)}`}>
                            {getBalanceText(party.current_balance)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  data-testid={`party-actions-${party.id}`}
                                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canEditParty && (
                                  <DropdownMenuItem onClick={() => handleEditParty(party)}>
                                    Edit
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem asChild>
                                  <Link to={`/business/${businessId}/customers/${party.id}`}>
                                    View Ledger
                                  </Link>
                                </DropdownMenuItem>
                                {canDeleteParty && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => handleDeleteParty(party)}
                                      className="text-red-600 focus:text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View - CALM PREMIUM POLISH */}
              <div className="lg:hidden space-y-3 p-4">
                {parties.map((party) => (
                  <div key={party.id} className="premium-card p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 text-sm">{party.name}</h3>
                        {party.gstin && (
                          <p className="text-xs text-gray-500 mt-1">GSTIN: {party.gstin}</p>
                        )}
                      </div>
                      <span className="premium-status premium-status-neutral capitalize text-xs ml-2">
                        {party.type}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Phone:</span>
                        <p className="text-sm text-gray-700">{party.phone || '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Balance:</span>
                        <p className={`text-sm font-medium ${getBalanceColor(party.current_balance)}`}>
                          {getBalanceText(party.current_balance)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-end pt-3 border-t border-gray-100">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEditParty && (
                            <DropdownMenuItem onClick={() => handleEditParty(party)}>
                              Edit
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem asChild>
                            <Link to={`/business/${businessId}/customers/${party.id}`}>
                              View Ledger
                            </Link>
                          </DropdownMenuItem>
                          {canDeleteParty && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDeleteParty(party)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
      </div>

      {/* Party Modal */}
      {showModal && canCreateParty && (
        <AddPartyModal
          party={selectedParty}
          onClose={() => setShowModal(false)}
          onSave={handleSaveParty}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
        </>
      )}
    </div>
  );
};

export default PartiesList;
